import { useDispatch } from "react-redux"
import { useMutation, type UseMutationResult } from "@tanstack/react-query"
import { useGetQuery } from "hooks/queries"
import { Creators as ApiWrapperCreators } from "model/apiWrapper"
import { Creators as NotificationCreators } from "modules/notifications/model"
import type { OAuthConnection, OAuthConnectionsResponse } from "../types"

interface ApiErrorLike {
  errors?: string | string[]
  response?: string
}

const CONNECTIONS_PATH = "/api/user/oauth/connections"

type ConnectionsQueryResult = ReturnType<typeof useGetQuery<OAuthConnectionsResponse>>
type DeleteConnectionMutateAsync = UseMutationResult<unknown, unknown, string>["mutateAsync"]

export type ConnectionsHookResult = Pick<ConnectionsQueryResult, "isLoading" | "isError" | "refetch"> & {
  connections: OAuthConnection[]
  deleteConnection: DeleteConnectionMutateAsync
}

export type UseConnectionsResult = ConnectionsQueryResult & ConnectionsHookResult

export type UseConnectionsHook = () => ConnectionsHookResult

type FullUseConnectionsHook = () => UseConnectionsResult

const useConnections: FullUseConnectionsHook = (): UseConnectionsResult => {
  const dispatch = useDispatch()

  const connectionsQuery = useGetQuery<OAuthConnectionsResponse>({
    url: CONNECTIONS_PATH,
    key: ["oauth-connections"],
    enabled: true,
  })

  const deleteConnectionMutation = useMutation<unknown, unknown, string>({
    mutationFn: (connectionId) =>
      new Promise((resolve, reject) => {
        dispatch(
          ApiWrapperCreators.delete(
            `${CONNECTIONS_PATH}/${connectionId}`,
            undefined,
            resolve,
            reject,
          ),
        )
      }),
    onError: (error) => {
      dispatch(NotificationCreators.add("error", null, getDeleteErrorMessage(error)))
    },
  })

  return {
    ...connectionsQuery,
    connections: connectionsQuery.data?.connections ?? [],
    deleteConnection: deleteConnectionMutation.mutateAsync,
  }
}

export default useConnections

function getDeleteErrorMessage(error: unknown) {
  if (typeof error !== "object" || error === null) {
    return "Unable to delete the connection. Please try again."
  }
  const apiError = error as ApiErrorLike

  if (apiError.errors) {
    return Array.isArray(apiError.errors) ? apiError.errors.join(", ") : apiError.errors
  }

  if (apiError.response) {
    return apiError.response
  }

  return "Unable to delete the connection. Please try again."
}
